/**
 * Formatação em pt-BR, compartilhada.
 *
 * Mora em `lib/` e não numa feature porque casca e telas formatam a mesma
 * data do mesmo jeito: duas cópias divergem, e a divergência aparece como a
 * mesma data escrita de dois jeitos em telas vizinhas.
 */

import { ApiError } from '@/lib/api';

const RELATIVO = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

const DATA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DATA_LONGA = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Distância até agora — "há 2 dias", "em 40 minutos".
 *
 * Numa fila de trabalho a distância é o que orienta a decisão; a data
 * absoluta fica no `title`, para quem precisa dela.
 */
export function formatarDistancia(iso: string | null | undefined, agora = Date.now()): string {
  if (!iso) return '—';
  const minutos = Math.round((new Date(iso).getTime() - agora) / 60_000);
  const absoluto = Math.abs(minutos);

  if (absoluto < 60) return RELATIVO.format(minutos, 'minute');
  if (absoluto < 60 * 24) return RELATIVO.format(Math.round(minutos / 60), 'hour');
  if (absoluto < 60 * 24 * 30) return RELATIVO.format(Math.round(minutos / (60 * 24)), 'day');
  return RELATIVO.format(Math.round(minutos / (60 * 24 * 30)), 'month');
}

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  return DATA_HORA.format(new Date(iso));
}

export function formatarDataLonga(data: Date): string {
  return DATA_LONGA.format(data);
}

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—';
  return DATA.format(new Date(iso));
}

/** Minutos de tempo útil como "2 h 30 min". Zero é "—" e não "0 min". */
export function formatarMinutos(minutos: number): string {
  if (minutos <= 0) return '—';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto} min`;
  if (resto === 0) return `${horas} h`;
  return `${horas} h ${resto} min`;
}

export function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatarPorcentagem(fracao: number): string {
  return `${Math.round(fracao * 100)}%`;
}

/**
 * Mensagem de erro para a tela.
 *
 * A `ApiError` já carrega a mensagem que o servidor escreveu em português —
 * "O chamado foi alterado por outra pessoa", "Status X não existe" — e ela
 * diz muito mais do que um "erro inesperado" genérico.
 */
export function mensagemDeErro(erro: unknown): string | null {
  if (!erro) return null;
  if (erro instanceof ApiError) return erro.message;
  if (erro instanceof Error) return erro.message;
  return 'Algo deu errado.';
}
